import { connect } from 'react-redux';
import { memo, useCallback, useContext, useMemo } from 'react';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IErknmAgreementResultDecisionSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IErknmAgreementResultDecisionSelectOption';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IGeneralResultDecision } from 'KnmErknmPackage/ErkmAgreementsModule/interfaces/IGeneralResultDecision';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { KnmResponsibleTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResponsibleTypeCodesEnum';

interface IAgreementsResultDecisionProps {
  id: string;
  name: string;
  watchOnly: boolean;
  resultDecisions: IGeneralResultDecision[] | undefined;
  agreements: IErknmFormAgreement[];
}

interface IAgreementsResultDecisionStoreProps {
  formRef: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

const AgreementsResultDecisionWithoutStore = memo(
  ({
    id,
    name,
    watchOnly,
    resultDecisions,
    agreements,
    decisionFormRef,
    formRef,
  }: IAgreementsResultDecisionProps & IAgreementsResultDecisionStoreProps) => {
    const { isKnm, isPm, isDecision } = useContext(AgreementsContext);

    const resultDecisionSelectOptions = useMemo(() => {
      return resultDecisions
        ?.filter(decision => {
          return (
            (decision.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
              decision.kindDecision?.code === KindResultDecisionCodesEnum.D_7) &&
            decision.status !== KnmResultDecisionStatusEnum.CANCELED &&
            decision.signed &&
            decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED &&
            (decision.organization?.subjectType?.code === KnmResponsibleTypeCodesEnum.UR ||
              decision.organization?.subjectType?.code === KnmResponsibleTypeCodesEnum.IUR ||
              decision.organization?.subjectType?.code === KnmResponsibleTypeCodesEnum.FIUR) &&
            !agreements?.find(
              agreement =>
                agreement?.prescription?.id === decision.guid &&
                (agreement.status === AgreementStatusEnum.DRAFT ||
                  agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
                  agreement.status === AgreementStatusEnum.ON_APPROVAL ||
                  agreement.status === AgreementStatusEnum.APPROVED ||
                  agreement.status === AgreementStatusEnum.COMPLETED),
            )
          );
        })
        .map(decision => {
          return {
            id: decision.guid || '',
            value: decision.guid,
            // @ts-ignore
            label: `Решение №${decision.numberDecision || decision.number}`,
          } as IErknmAgreementResultDecisionSelectOption;
        });
    }, [agreements, resultDecisions]);

    /**
     * Функция очищения формы, если начения изменилось или удалили
     */
    const clearForm = useCallback(() => {
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.violations`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.approvalOfAgreement`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.approvalOfAgreementAttachment`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.eliminateViolationsNumber`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.conclusionDate`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.eliminateViolationsAttachment`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.termOfExecutionAgreement`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.applicationForConsiderationAttachment`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.violationCorrectionProgramAttachments`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.otherAttachments`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.fioSigner`);
      // @ts-ignore
      (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.titleSigner`);
    }, [isDecision, decisionFormRef, formRef]);

    return (
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Предписание',
          field: `${name}.prescription`,
          isKnm,
          isPm,
          isDecision,
        })}
        id={`${id}.prescription`}
      >
        <SelectInputField
          name={`${name}.prescription`}
          placeholder="Выберите предписание"
          size="medium"
          options={resultDecisionSelectOptions}
          disabled={watchOnly}
          onChange={clearForm}
        />
      </FieldBlock>
    );
  },
);

const mapStateToProps = (state: IRootState): IAgreementsResultDecisionStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const AgreementsResultDecision = connect(mapStateToProps)(AgreementsResultDecisionWithoutStore);
